import type { DashboardAssistantDockMessage } from '../../../../contexts/DashboardAssistantDockContext';

export type AssistantSuggestedReply = {
  label: string;
  prompt: string;
};

const DEFAULT_ANALYTICS_SUGGESTIONS = [
  'Show revenue by month',
  'Show payment status breakdown',
  'Show top forms by revenue',
];

const PAGE_AWARE_ANALYTICS_SUGGESTIONS: Array<{
  paths: string[];
  suggestions: string[];
}> = [
  {
    paths: ['/', '/home'],
    suggestions: [
      'Show revenue by month',
      'Show payment methods by revenue',
      'Show top forms by revenue',
    ],
  },
  {
    paths: ['/payments'],
    suggestions: [
      'Show payment status breakdown',
      'Show payment methods by revenue',
      'Show payment issues by day',
    ],
  },
  {
    paths: ['/forms'],
    suggestions: [
      'Show top forms by revenue',
      'Show form type breakdown',
      'Show checkout bump performance',
    ],
  },
  {
    paths: ['/elements'],
    suggestions: [
      'Show checkout bump performance',
      'Show monthly bump conversion rate',
      'Show form type breakdown',
    ],
  },
  {
    paths: ['/contacts'],
    suggestions: [
      'Show top contacts by revenue',
      'Show recurring vs one-time revenue',
      'Show payment issues by day',
    ],
  },
  {
    paths: ['/subscriptions'],
    suggestions: [
      'Show recurring vs one-time revenue',
      'Show recurring share for the last 90 days',
      'Show payment issues by day',
    ],
  },
  {
    paths: ['/payouts'],
    suggestions: [
      'Show revenue by month',
      'Show payment status breakdown',
      'Show payment methods by revenue',
    ],
  },
  {
    paths: ['/broadcasts', '/segments', '/brand-kit', '/marketing'],
    suggestions: [
      'Show UTM breakdown',
      'Show revenue by month',
      'Show top contacts by revenue',
    ],
  },
];

// Smart Tagging sessions (AI Tag Automation, initiatives/ai-tag-automation):
// the chat is the harness — the same empty state as any session, with
// SESSION-aware chips (this set) instead of the page-aware analytics ones.
// Phrased to map cleanly onto resolve_contact_selection criteria.
const SMART_TAGGING_SUGGESTIONS = [
  'Tag everyone who donated 3 or more times this year',
  'Tag contacts who gave under $50 this year',
  'Tag contacts who gave over $500 lifetime',
  'Tag everyone who donated at least once this year',
];

const MAX_SUGGESTED_REPLY_COUNT = 3;

export function getSmartTaggingSuggestions() {
  return SMART_TAGGING_SUGGESTIONS;
}

export function getPageAwareAnalyticsSuggestions(pathname: string) {
  const normalizedPath = normalizeDashboardPath(pathname);
  const match = PAGE_AWARE_ANALYTICS_SUGGESTIONS.find(({ paths }) =>
    paths.some((path) => isDashboardPathMatch(normalizedPath, path)),
  );

  return match?.suggestions ?? DEFAULT_ANALYTICS_SUGGESTIONS;
}

export function getSuggestedRepliesForLatestAssistantMessage(
  messages: DashboardAssistantDockMessage[],
): AssistantSuggestedReply[] {
  const latestMessage = messages[messages.length - 1];

  if (
    !latestMessage ||
    latestMessage.role !== 'assistant' ||
    latestMessage.kind === 'action' ||
    latestMessage.displayPayload ||
    !latestMessage.content.trim()
  ) {
    return [];
  }

  return getSuggestedRepliesFromAssistantContent(latestMessage.content);
}

export function getSuggestedRepliesFromAssistantContent(
  content: string,
): AssistantSuggestedReply[] {
  const normalizedContent = normalizeAssistantContent(content);
  const lowerContent = normalizedContent.toLowerCase();

  if (!hasOptionCue(normalizedContent, lowerContent)) {
    return [];
  }

  const ruleBasedReplies = getRuleBasedSuggestedReplies(lowerContent);
  if (ruleBasedReplies.length > 0) {
    return dedupeSuggestedReplies(ruleBasedReplies).slice(0, MAX_SUGGESTED_REPLY_COUNT);
  }

  const replyContext = getLatestReplyContext(content);
  const questionReplies = extractQuestionSuggestedReplies(replyContext.question);
  const directOfferReplies = extractDirectOfferSuggestedReplies(replyContext.offer);
  const bulletReplies = extractBulletSuggestedReplies(replyContext.offer);
  const fallbackBoldReplies =
    questionReplies.length === 0 && directOfferReplies.length === 0 && bulletReplies.length === 0
      ? extractBoldSuggestedReplies(replyContext.question || replyContext.offer)
      : [];

  return dedupeSuggestedReplies([
    ...questionReplies,
    ...directOfferReplies,
    ...bulletReplies,
    ...fallbackBoldReplies,
  ]).slice(0, MAX_SUGGESTED_REPLY_COUNT);
}

function getLatestReplyContext(content: string) {
  const questions = Array.from(content.matchAll(/[^.!?]*\?/g));
  const latestQuestion = [...questions]
    .reverse()
    .find((candidate) => hasOptionCue(candidate[0], candidate[0].toLowerCase()));
  const offerCue = /\b(?:if you want|would you like|would you prefer|i can also|i can show|choose)\b/gi;
  let offerStart = -1;

  for (const match of content.matchAll(offerCue)) {
    offerStart = match.index ?? offerStart;
  }

  const questionStart = latestQuestion?.index ?? -1;
  const questionEnd = questionStart + (latestQuestion?.[0].length ?? 0);
  const questionIsLatestContext =
    questionStart >= 0 &&
    (offerStart < 0 || questionStart >= offerStart || offerStart < questionEnd);

  return {
    offer: !questionIsLatestContext && offerStart >= 0 ? content.slice(offerStart).trim() : '',
    question: questionIsLatestContext ? latestQuestion?.[0].trim() ?? '' : '',
  };
}

function extractQuestionSuggestedReplies(question: string): AssistantSuggestedReply[] {
  if (!question) {
    return [];
  }

  const cleanedQuestion = cleanOptionText(question);
  const choiceBody = cleanedQuestion
    .replace(/^.*?\b(?:would you like|would you prefer|do you want|do you mean|which)\b(?:\s+me\s+to)?\s*/i, '')
    .replace(/^.*?:\s*/, '')
    .trim();
  const alternatives = choiceBody
    .split(/\s+(?:or)\s+/i)
    .map((option) => cleanOptionText(option.replace(/^(?:to\s+)?/i, '')))
    .filter(isActionableOptionText);

  if (alternatives.length >= 2) {
    return alternatives.map(replyFromOptionText);
  }

  const confirmationMatch = cleanedQuestion.match(
    /^(?:would you like|do you want|should i|can i)\s+(?:me\s+to\s+)?(.+)$/i,
  );
  if (!confirmationMatch) {
    return [];
  }

  const action = cleanOptionText(confirmationMatch[1]);
  if (!isActionableOptionText(action)) {
    return [];
  }

  return [
    {
      label: labelFromOptionText(action),
      prompt: promptFromOptionText(action),
    },
    { label: 'No, thanks', prompt: 'No, thanks' },
  ];
}

function extractDirectOfferSuggestedReplies(offer: string): AssistantSuggestedReply[] {
  if (!offer) {
    return [];
  }

  const normalizedOffer = normalizeAssistantContent(offer);
  const match = normalizedOffer.match(
    /^(?:if you want,?\s*)?i can(?: also)?\s+(.+?)(?:[.!?]|$)/i,
  );
  if (!match) {
    return [];
  }

  const action = cleanOptionText(match[1].replace(/\s+for you$/i, '')).replace(
    /^help you (?:to )?/i,
    '',
  );
  if (action.includes(':') || !isActionableOptionText(action)) {
    return [];
  }

  return [replyFromOptionText(action)];
}

function replyFromOptionText(option: string): AssistantSuggestedReply {
  return {
    label: labelFromOptionText(option),
    prompt: promptFromOptionText(option),
  };
}

function normalizeDashboardPath(pathname: string) {
  const withoutDashboardBase = pathname.replace(/^\/dashboard(?=\/|$)/, '') || '/';
  const withoutTrailingSlash = withoutDashboardBase.replace(/\/+$/, '');
  return withoutTrailingSlash || '/';
}

function isDashboardPathMatch(pathname: string, path: string) {
  if (path === '/') {
    return pathname === '/';
  }

  return pathname === path || pathname.startsWith(`${path}/`);
}

function normalizeAssistantContent(content: string) {
  return content.replace(/\s+/g, ' ').trim();
}

function hasOptionCue(content: string, lowerContent: string) {
  if (
    /\b(?:do you mean|clarification|which|would you like|would you prefer|i can also show|i can show|if you want|choose|option)\b/.test(
      lowerContent,
    )
  ) {
    return true;
  }

  return content.includes('?') && /\sor\s/.test(lowerContent);
}

function getRuleBasedSuggestedReplies(lowerContent: string): AssistantSuggestedReply[] {
  if (lowerContent.includes('checkout bump')) {
    const replies: AssistantSuggestedReply[] = [];

    if (lowerContent.includes('revenue') || lowerContent.includes('how much')) {
      replies.push({
        label: 'Checkout bump revenue',
        prompt: 'Show checkout bump revenue for the last 90 days',
      });
    }

    if (
      lowerContent.includes('conversion') ||
      lowerContent.includes('converted') ||
      lowerContent.includes('percentage') ||
      lowerContent.includes('percent')
    ) {
      replies.push({
        label: 'Monthly bump conversion rate',
        prompt: 'Show monthly bump conversion rate for the last 90 days',
      });
    }

    if (replies.length === 0) {
      replies.push(
        {
          label: 'Checkout bump performance',
          prompt: 'Show checkout bump performance for the last 90 days',
        },
        {
          label: 'Checkout bump attach rate',
          prompt: 'Show checkout bump attach rate for the last 90 days',
        },
      );
    }

    return replies;
  }

  if (lowerContent.includes('chart') && lowerContent.includes('table')) {
    return [
      { label: 'Show as chart', prompt: 'Show this as a chart' },
      { label: 'Show as table', prompt: 'Show this as a table' },
    ];
  }

  if (
    lowerContent.includes('revenue') &&
    (lowerContent.includes('payment count') ||
      lowerContent.includes('number of payments') ||
      lowerContent.includes('count'))
  ) {
    return [
      { label: 'Revenue', prompt: 'Show revenue by month for the last 30 days' },
      { label: 'Payment count', prompt: 'Show payment count by month for the last 30 days' },
      { label: 'Average payment', prompt: 'Show average payment by month for the last 30 days' },
    ];
  }

  // Offer periods only when the reply asks for one, never after it says a
  // period can't be used (QA 2026-10-12, AST-10: the chips offered the very
  // windows the reply had just refused).
  if (
    (lowerContent.includes('date range') || lowerContent.includes('time period')) &&
    !PERIOD_REFUSAL_PATTERN.test(lowerContent)
  ) {
    return [
      { label: 'Last 30 days', prompt: 'Use the last 30 days' },
      { label: 'Last 90 days', prompt: 'Use the last 90 days' },
      { label: 'Year to date', prompt: 'Use year to date' },
    ];
  }

  return [];
}

const PERIOD_REFUSAL_PATTERN =
  /isn['’]t available|aren['’]t available|not available|doesn['’]t (?:currently )?support|don['’]t (?:currently )?support|not supported|unsupported|can['’]t|cannot|unable to/;

function extractBulletSuggestedReplies(content: string): AssistantSuggestedReply[] {
  return content
    .split('\n')
    .filter((line) => /^\s*[-*\u2022]\s+/.test(line))
    .map((line) => cleanOptionText(line.replace(/^\s*[-*\u2022]\s+/, '')))
    .filter(isActionableOptionText)
    .map((option) => ({
      label: labelFromOptionText(option),
      prompt: promptFromOptionText(option),
    }));
}

function extractBoldSuggestedReplies(content: string): AssistantSuggestedReply[] {
  return Array.from(content.matchAll(/\*\*([^*]{4,140})\*\*/g))
    .map((match) => cleanOptionText(match[1]))
    .filter(isActionableOptionText)
    .map((option) => ({
      label: labelFromOptionText(option),
      prompt: promptFromOptionText(option),
    }));
}

function cleanOptionText(text: string) {
  return text
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[.:;?]+$/, '')
    .trim();
}

function isActionableOptionText(text: string) {
  const lowerText = text.toLowerCase();

  if (text.length < 4 || text.length > 140) {
    return false;
  }

  if (/^(success|failed|pending|refunded|disputed|total shown)\b/.test(lowerText)) {
    return false;
  }

  return /\b(?:show|open|view|check|create|continue|apply|use|compare|list|rank|chart|table|breakdown|last \d+ days|revenue|payment|form|fund|contact|ticket|status|method|settings|embed|utm|bump|recurring|one-time|issue|trend|count|average|share|rate|conversion)\b/.test(
    lowerText,
  );
}

function labelFromOptionText(text: string) {
  const settingsMatch = text.match(/^open\s+(?:the\s+)?(.+?)\s+settings page(?:\s+in CharityStack)?$/i);
  if (settingsMatch) {
    return `Open ${settingsMatch[1]} settings`;
  }

  const workspaceMatch = text.match(
    /^open\s+(?:the\s+)?(.+?)\s+workspace(?:\s+in CharityStack)?$/i,
  );
  if (workspaceMatch) {
    return `Open ${workspaceMatch[1]} workspace`;
  }

  if (text.length > 56 && /\s+and\s+/i.test(text)) {
    const primaryAction = text
      .split(/\s+and\s+/i)[0]
      .replace(/\s+in CharityStack$/i, '')
      .replace(/^open\s+the\s+/i, 'Open ');
    return primaryAction.charAt(0).toUpperCase() + primaryAction.slice(1);
  }

  const normalized = text
    .replace(/^show\s+/i, '')
    .replace(/^(?:the|a)\s+/i, '')
    .replace(/\s+in CharityStack$/i, '');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function promptFromOptionText(text: string) {
  if (/^(show|open|view|check|create|continue|apply|use|compare|list|rank)\b/i.test(text)) {
    return text;
  }

  if (/^a\s+chart\b/i.test(text) || /^chart\b/i.test(text)) {
    return 'Show this as a chart';
  }

  if (/^a\s+table\b/i.test(text) || /^table\b/i.test(text)) {
    return 'Show this as a table';
  }

  return `Show ${text}`;
}

// Buttons that live on a card. A chip with the same words sends plain text
// and skips what the button does first: Use this list saves pending unchecks
// before it tells the assistant the list is ready (prod test 2026-10-14, C1;
// QA R3-02). So a card action is never offered as a chip. The match is
// unanchored: a chip that only CONTAINS the action ("Use this list,", "To use
// this list or see more donors") skips the button just the same (review of
// #4431, F1).
const CARD_ACTION_REPLY_PATTERN = /\buse (?:this|the) list\b/i;

function isCardActionReply(reply: AssistantSuggestedReply) {
  return [reply.label, reply.prompt].some((text) => CARD_ACTION_REPLY_PATTERN.test(text));
}

function dedupeSuggestedReplies(replies: AssistantSuggestedReply[]) {
  const seenPrompts = new Set<string>();
  return replies.filter((reply) => {
    const promptKey = reply.prompt.trim().toLowerCase();
    if (!promptKey || seenPrompts.has(promptKey) || isCardActionReply(reply)) {
      return false;
    }

    seenPrompts.add(promptKey);
    return true;
  });
}
